import { TTarget } from '@/types';
import { Logo, Typography } from '@data-products-and-ai/react-components';
import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import bg from '@/assets/bg.jpg';
import face0 from '@/assets/faces/0.png';
import face1 from '@/assets/faces/1.png';
import face2 from '@/assets/faces/2.png';
import face3 from '@/assets/faces/3.png';
import face4 from '@/assets/faces/4.png';
import face5 from '@/assets/faces/5.png';
import face6 from '@/assets/faces/6.png';
import face7 from '@/assets/faces/7.png';
import face8 from '@/assets/faces/8.png';
import face9 from '@/assets/faces/9.png';

const faceArr = [
  face0,
  face1,
  face2,
  face3,
  face4,
  face5,
  face6,
  face7,
  face8,
  face9,
];

const arrNumber = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const arrLetters = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
];

type LockProps = {
  lockType?: 'number' | 'letter' | 'image';
};

type TLock = {
  hasLogo?: boolean;
  text?: ReactNode;
  target: TTarget;
  combination: string;
} & LockProps;

const LockColumn = ({
  lockType = 'number',
  onValueChange,
  initialValue,
}: LockProps & {
  onValueChange: (value: string) => void;
  initialValue: string;
}) => {
  const [index, setIndex] = useState(() => {
    if (lockType === 'number') {
      return arrNumber.indexOf(initialValue);
    } else if (lockType === 'letter') {
      return arrLetters.indexOf(initialValue);
    } else {
      return parseInt(initialValue, 10);
    }
  });

  const arrImages = Array.from({ length: 10 }, (_, i) => `${i}`);
  const arr =
    lockType === 'number'
      ? arrNumber
      : lockType === 'letter'
      ? arrLetters
      : arrImages;

  const handleClick = () => {
    const newIndex = (index + 1) % arr.length;
    setIndex(newIndex);
    onValueChange(arr[newIndex]);
  };

  if (lockType === 'image') {
    return (
      <button
        style={{
          padding: 0,
          width: 80,
          height: 80,
          border: '1px solid black',
          margin: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#3383a8',
        }}
        onClick={() => handleClick()}
      >
        <img
          src={faceArr[index]}
          alt={arr[index]}
          style={{ maxWidth: '100%', maxHeight: '100%' }}
        />
      </button>
    );
  }

  return (
    <button
      style={{
        fontSize: 30,
        padding: 20,
        width: 80,
        height: 120,
        border: '1px solid black',
        margin: 5,
      }}
      onClick={() => handleClick()}
    >
      {arr[index]}
    </button>
  );
};
const RandomSentence = () => {
  const [randomSentence, setRandomSentence] = useState('');

  useEffect(() => {
    const sentences = [
      'Keep up the excellent work!',
      'Well done!',
      'Fantastic job!',
      'Keep it going!',
      'Great job!',
      'Excellent work!',
    ];
    setRandomSentence(sentences[Math.floor(Math.random() * sentences.length)]);
  }, []);

  return (
    <>
      <Typography tag="d2">CORRECT!</Typography>
      <div
        style={{
          fontFamily: 'Siemens',
          fontSize: '1.5rem',
          lineHeight: '1.5',
        }}
        dangerouslySetInnerHTML={{ __html: randomSentence }}
      />
    </>
  );
};

const Lock = ({
  target,
  combination,
  text,
  lockType = 'number',
  hasLogo = true,
}: TLock) => {
  const [correct, setCorrect] = useState(false);
  const navigate = useNavigate();
  const adaptedCombination =
    lockType === 'image' ? combination.replace(/,/g, '') : combination;

  const generateRandomCombination = (
    length: number,
    type: 'number' | 'letter' | 'image',
  ): string[] => {
    const result = [];
    for (let i = 0; i < length; i++) {
      if (type === 'number') {
        result.push(arrNumber[Math.floor(Math.random() * arrNumber.length)]);
      } else if (type === 'letter') {
        result.push(arrLetters[Math.floor(Math.random() * arrLetters.length)]);
      } else {
        result.push(arrNumber[Math.floor(Math.random() * arrNumber.length)]); // Using numbers as index for images
      }
    }
    return result;
  };

  const [currentCombination, setCurrentCombination] = useState<string[]>(
    generateRandomCombination(adaptedCombination.length, lockType),
  );

  const isCombinationCorrect =
    currentCombination.join('') === adaptedCombination;

  useEffect(() => {
    if (isCombinationCorrect) {
      setCorrect(true);
      setTimeout(() => {
        navigate(target);
      }, 4000);
      window.scrollTo(0, 0);
    }
  }, [isCombinationCorrect, target, navigate]);

  const handleColumnChange = (index: number, value: string) => {
    const newCombination = [...currentCombination];
    newCombination[index] = value;
    setCurrentCombination(newCombination);
  };

  if (correct)
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          backgroundSize: 'cover',
          backgroundImage: `url("${bg}")`,
          color: 'white',
        }}
      >
        <div style={{ padding: 30 }}>
          <Typography tag="h1">
            <RandomSentence />
          </Typography>
        </div>
      </div>
    );

  return (
    <>
      {hasLogo && (
        <div style={{ padding: 20, textAlign: 'center' }}>
          <Logo />
        </div>
      )}
      <div style={{ padding: 20, textAlign: 'center' }}>{text}</div>

      <div
        style={{
          display: 'flex',
          alignContent: 'center',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
        }}
      >
        {adaptedCombination.split('').map((char, index) => (
          <LockColumn
            key={`${adaptedCombination}-${index}`}
            lockType={lockType}
            onValueChange={(value) => handleColumnChange(index, value)}
            initialValue={currentCombination[index]}
          />
        ))}
      </div>
    </>
  );
};

export default Lock;
